import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import throttle from 'lodash/throttle';
import { Image } from '@/components/common';
import bridge from '@/utils/bridge';
import cssStyles from './TeamButton.css';
import SortableItem from '../../widgets/sortable/SortableItem';

/**
 * @class TeamButton
 * @classdesc Button for a sessionf
 *
 * @extends {Component}
 *
 * @param {object} props
 * @param {number} props.index Item index in the list
 * @param {string} props.id Session ID
 * @param {string} props.sessionInfo All session related data, eg. teamName etc.
 * @param {boolean} [props.isActive] If this session is the current active session
 * @param {number} [props.unreadConversationCount] Number of chats with unread messages for this session
 * @param {boolean} [props.hasMention] If this session has chats with unread mentioned messages
 * @param {string} [props.keyboardShortcut] This button's keyboard shortcut
 * @param {boolean} [props.disableTooltip] Disable this button's tooltip
 * @param {function} [props.onClick] Callback that handles this button's click
 */
class TeamButton extends Component {
  static defaultProps = {
    disableContextMenu: false,
    tooltipPosition: false,
    isMenuTeamSwitcher: false,
  };

  state = {
    showLeaveTeam: false,
    contextMenuLeft: '0px',
    contextMenuTop: '0px',
  };

  imageLoaded = false;

  loadAvatar = throttle(() => {
    this.setState(() => {
      if (this.imageLoaded) {
        return null;
      }
      return {
        avatar: this.getAvatar(),
      };
    });
  }, 200);

  teamButton = React.createRef();

  constructor(props) {
    super(props);
    this.subscriptions = [
      bridge.subscribe('/connectionState/changed', this.loadAvatar),
    ];
  }

  componentDidMount() {
    this.loadAvatar();
  }

  componentWillUnmount() {
    bridge.unsubscribeAll(this.subscriptions);
  }

  onError = () => {
    this.imageLoaded = false;
  };

  onLoad = () => {
    this.imageLoaded = true;
  };

  onButtonMouseDown = event => {
    if (event.button !== 2) {
      const { onClick, id } = this.props;
      onClick(id, 'team_button');
    }
    event.stopPropagation();
    event.preventDefault();
    this.focusTeamButton();
  };

  onBlur = () => {
    this.setState(() => {
      return {
        showLeaveTeam: false,
      };
    });
  };

  onLeaveTeam = event => {
    const { onLeaveTeamClick, id } = this.props;
    onLeaveTeamClick(id, 'team_button');
    this.onBlur();
    event.stopPropagation();
    event.preventDefault();
  };

  onContextMenuClick = event => {
    const teamButton = this.teamButton.current;
    if (!teamButton) {
      return;
    }
    this.focusTeamButton();
    const teamButtonRect = teamButton.getBoundingClientRect();

    const topBannerHeight =
      document.querySelector('#promptsRoot').innerHTML === '' ? 0 : 90;

    this.setState(() => {
      return {
        showLeaveTeam: true,
        contextMenuLeft: teamButtonRect.left + teamButtonRect.width,
        contextMenuTop:
          (teamButtonRect.top + teamButtonRect.bottom - topBannerHeight - 35) /
          2,
      };
    });
    event.stopPropagation();
    event.preventDefault();
  };

  getKeyboardShortcut() {
    const { keyboardShortcut } = this.props;

    if (keyboardShortcut) {
      return (
        <div className={cssStyles.keyboardShortcut}>{keyboardShortcut}</div>
      );
    }

    return '';
  }

  getFirstLetter = name => {
    return name.charAt(0).toUpperCase();
  };

  getScrollBadgeClass() {
    const {
      mutedUnreadChatCount,
      hasMention,
      unreadConversationCount,
    } = this.props;

    if (hasMention) {
      return 'mentioned';
    }

    if (unreadConversationCount) {
      return 'unread';
    }

    if (mutedUnreadChatCount > 0) {
      return 'mutedUnread';
    }

    return '';
  }

  getUnreadBadge() {
    const {
      unreadConversationCount,
      hasMention,
      mutedUnreadChatCount,
    } = this.props;
    const messageCountLimit = 25;
    if (unreadConversationCount === 0 && mutedUnreadChatCount === 0) {
      return null;
    }
    const badgeCounterClassName = classNames(cssStyles.teamButtonBadge, {
      [cssStyles.badgeWithCounter]: unreadConversationCount > 0,
      [cssStyles.badgeWithMentions]: hasMention,
      [cssStyles.badgeMuted]:
        unreadConversationCount === 0 && mutedUnreadChatCount > 0,
    });

    return (
      <div className={classNames(badgeCounterClassName)}>
        {unreadConversationCount > 0 &&
          (unreadConversationCount > messageCountLimit
            ? `${messageCountLimit}+`
            : unreadConversationCount)}
      </div>
    );
  }

  getAvatar() {
    const {
      sessionInfo: {
        teamInfo: { teamAvatar },
      },
    } = this.props;
    if (teamAvatar) {
      return `${teamAvatar}?t=${new Date()}`;
    }
    return null;
  }

  focusTeamButton() {
    const teamButton = this.teamButton.current;
    if (!teamButton) {
      return;
    }

    teamButton.focus();
  }

  render() {
    const {
      sessionInfo: {
        teamInfo: { teamName: name },
      },
      index,
      isActive,
      disableTooltip,
      tooltipPosition,
      disableContextMenu,
      isMenuTeamSwitcher,
    } = this.props;

    const { avatar } = this.state;

    // Define button's classNames
    const teamButtonClassName = classNames(cssStyles.teamButton, {
      [cssStyles.isActive]: isActive,
      [cssStyles.app_menu_switcher]: isMenuTeamSwitcher,
    });

    // Tooltip options

    const {
      showLeaveTeam,
      contextMenuLeft: left,
      contextMenuTop: top,
    } = this.state;
    const contextMenuStyle = {
      left: `${left - 5}px`,
      top: `${top}px`,
    };

    const contextMenuClassName = classNames(
      cssStyles.contextMenu,
      'leaveTeamContextMenu'
    );

    const shouldHideToolTip = disableTooltip || showLeaveTeam;

    const tooltip = {
      'data-tip': name,
      'data-place': tooltipPosition,
      'data-effect': 'solid',
      'data-tip-disable': shouldHideToolTip,
    };

    /* eslint-disable */

    // Returns the dom to be rendered
    return (
      <SortableItem index={index}>
        <div
          role='button'
          onKeyDown={() => {}}
          tabIndex={0}
          onMouseDown={this.onButtonMouseDown}
          status={this.getScrollBadgeClass()}
          onContextMenu={this.onContextMenuClick}
          onBlur={this.onBlur}
          ref={this.teamButton}
        >
          <div {...tooltip} ref={ref => (this.toolTipRef = ref)}>
            <div className={teamButtonClassName}>
              <div className={cssStyles.button}>
                {this.getUnreadBadge()}
                {avatar ? (
                  <Image
                    alt={name}
                    onError={this.onError}
                    onLoad={this.onLoad}
                    src={avatar}
                    className={cssStyles.avatar}
                  />
                ) : (
                  <div className={cssStyles.letter}>
                    {this.getFirstLetter(name)}
                  </div>
                )}
              </div>
              <div className={cssStyles.activeTeam} />
              {this.getKeyboardShortcut()}
            </div>
            {!isMenuTeamSwitcher && (
              <ReactTooltip className={cssStyles.tooltip} />
            )}
          </div>
          {showLeaveTeam && !disableContextMenu ? (
            <div
              role='button'
              onKeyDown={() => {}}
              tabIndex={0}
              style={contextMenuStyle}
              className={contextMenuClassName}
              onMouseDown={this.onLeaveTeam}
            >
              Leave Team
            </div>
          ) : null}
        </div>
      </SortableItem>
    );
  }
}

TeamButton.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  sessionInfo: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  unreadConversationCount: PropTypes.number,
  hasMention: PropTypes.bool,
  keyboardShortcut: PropTypes.string,
  disableTooltip: PropTypes.bool,
  disableContextMenu: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onLeaveTeamClick: PropTypes.func.isRequired,
  tooltipPosition: PropTypes.string,
  isMenuTeamSwitcher: PropTypes.bool,
};

TeamButton.defaultProps = {
  unreadConversationCount: 0,
  hasMention: false,
  keyboardShortcut: '',
  isActive: false,
  disableTooltip: false,
};

export default TeamButton;



// WEBPACK FOOTER //
// ./src/components/teamSwitcher/teamButton/TeamButton.js